import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Search, Grid, List, ChevronDown, ExternalLink, ClipboardCheck, Shield, MapPin, Gauge, Fuel, Settings, Star, TrendingUp, Heart, DollarSign, Award, Car, X, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const incomingData = location.state || {};
  
  const [listings, setListings] = useState(incomingData.listings || []);
  const [total, setTotal] = useState(incomingData.total || 0);
  const [page, setPage] = useState(incomingData.page || 1);
  const [pageSize] = useState(incomingData.pageSize || 24);
  const [sources, setSources] = useState(incomingData.sources || []);
  const [criteria, setCriteria] = useState(incomingData.criteria || {});
  
  const [loading, setLoading] = useState(!incomingData.listings?.length);
  const [sort, setSort] = useState('relevance');
  const [filters, setFilters] = useState({ fuel: '', transmission: '' });
  const [viewMode, setViewMode] = useState('grid');
  const [makes, setMakes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Additional filter state
  const [searchMinYear, setSearchMinYear] = useState('');
  const [searchFuel, setSearchFuel] = useState('');
  const [searchTransmission, setSearchTransmission] = useState('');

  // Search bar state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMake, setSearchMake] = useState('');
  const [searchMinPrice, setSearchMinPrice] = useState('');
  const [searchMaxPrice, setSearchMaxPrice] = useState('');
  const [searchBodyType, setSearchBodyType] = useState('');

  useEffect(() => {
    const make = criteria.make || '';
    const searchTitle = make 
      ? `${make} Cars for Sale in South Africa | AutoSearch SA`
      : 'Used Cars for Sale in South Africa | Compare Prices | AutoSearch SA';
    document.title = searchTitle;
  }, [criteria.make]);

  // Fetch makes for dropdown
  useEffect(() => {
    axios.get(`${API}/makes`).then(r => setMakes(r.data.makes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('query');
    const urlMake = params.get('make');
    const urlModel = params.get('model');
    const urlProvince = params.get('province');
    const urlBodyType = params.get('body_type');
    const urlMinPrice = params.get('min_price');
    const urlMaxPrice = params.get('max_price');
    const urlFuel = params.get('fuel');
    const urlTransmission = params.get('transmission');
    const urlMaxMileage = params.get('max_mileage');
    
    const newCriteria = { ...criteria };
    if (urlQuery) newCriteria.query = urlQuery;
    if (urlMake) newCriteria.make = urlMake;
    if (urlModel) newCriteria.model = urlModel;
    if (urlProvince) newCriteria.province = urlProvince;
    if (urlBodyType) newCriteria.bodyType = urlBodyType;
    if (urlMinPrice) newCriteria.minPrice = urlMinPrice;
    if (urlMaxPrice) newCriteria.maxPrice = urlMaxPrice;
    if (urlMaxMileage) newCriteria.maxMileage = urlMaxMileage;
    
    const newFilters = { ...filters };
    if (urlFuel) newFilters.fuel = urlFuel;
    if (urlTransmission) newFilters.transmission = urlTransmission;
    
    setCriteria(newCriteria);
    if (urlFuel || urlTransmission) setFilters(newFilters);

    // Sync search bar with URL params
    setSearchQuery(urlQuery || '');
    setSearchMake(urlMake || '');
    setSearchMinPrice(urlMinPrice || '');
    setSearchMaxPrice(urlMaxPrice || '');
    setSearchBodyType(urlBodyType || '');
    setSearchMinYear(params.get('min_year') || '');
    setSearchFuel(urlFuel || '');
    setSearchTransmission(urlTransmission || '');
    
    // Add min_year to criteria
    if (params.get('min_year')) newCriteria.minYear = params.get('min_year');
    
    fetchResultsWithCriteria(newCriteria, 1, sort, newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  
  const fetchResultsWithCriteria = async (searchCriteria, pageNum = 1, sortBy = sort, newFilters = filters) => {
    setLoading(true);
    try {
      const payload = {
        make: searchCriteria.make || '',
        model: searchCriteria.model || '',
        query: searchCriteria.query || '',
        min_price: searchCriteria.minPrice ? parseInt(searchCriteria.minPrice) : 0,
        max_price: searchCriteria.maxPrice ? parseInt(searchCriteria.maxPrice) : 5000000,
        min_year: searchCriteria.minYear ? parseInt(searchCriteria.minYear) : 0,
        province: searchCriteria.province || '',
        body_type: searchCriteria.bodyType || '',
        fuel: newFilters.fuel || '',
        transmission: newFilters.transmission || '',
        page: pageNum,
        page_size: pageSize,
        sort: sortBy
      };
      const res = await axios.post(`${API}/search`, payload);
      setListings(res.data.listings || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || pageNum);
      setSources(res.data.sources || []);
    } catch (e) {
      console.error('Fetch results failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (pageNum = 1, sortBy = sort, newFilters = filters) => {
    fetchResultsWithCriteria(criteria, pageNum, sortBy, newFilters);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (searchMake) params.set('make', searchMake);
    if (searchMinPrice) params.set('min_price', searchMinPrice);
    if (searchMaxPrice) params.set('max_price', searchMaxPrice);
    if (searchBodyType) params.set('body_type', searchBodyType);
    if (searchMinYear) params.set('min_year', searchMinYear);
    if (searchFuel) params.set('fuel', searchFuel);
    if (searchTransmission) params.set('transmission', searchTransmission);
    navigate(`/results?${params.toString()}`);
  };

const handleClearSearch = () => {
  setSearchQuery('');
  setSearchMake('');
  setSearchMinPrice('');
  setSearchMaxPrice('');
  setSearchBodyType('');
  setSearchMinYear('');
  setSearchFuel('');
  setSearchTransmission('');
  setFilters({ fuel: '', transmission: '' });
  navigate('/results');
};

  const handleSortChange = (newSort) => {
    setSort(newSort);
    fetchResults(1, newSort, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchResults(1, sort, newFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > Math.ceil(total / pageSize)) return;
    fetchResults(newPage, sort, filters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const trackClick = async (carId, actionType, url, source) => {
    try {
      await axios.post(`${API}/track/click`, null, {
        params: { car_id: carId, action_type: actionType, referral_url: url, source }
      });
    } catch (e) {}
  };

  const handleDealerClick = (car) => {
    trackClick(car.id, 'dealer_click', car.source_url, car.source);
    if (car.source_url && car.source_url !== '#') window.open(car.source_url, '_blank');
  };

  const handleInspectionClick = (car) => {
    trackClick(car.id, 'inspection_click', '/inspector', car.source);
    navigate('/inspector', { state: { car } });
  };

  const handleInsuranceClick = (car) => {
    trackClick(car.id, 'insurance_click', '/insurance', car.source);
    navigate('/insurance', { state: { car } });
  };

  const handleFinanceClick = (car) => {
    trackClick(car.id, 'finance_click', '/finance', car.source);
    navigate('/finance', { state: { car } });
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    return `R ${price.toLocaleString('en-ZA')}`;
  };

  const formatMileage = (mileage) => {
    if (!mileage) return null;
    return `${mileage.toLocaleString('en-ZA')} km`;
  };

  const calculateMonthly = (price) => {
    if (!price) return null;
    const rate = 0.13 / 12;
    const term = 72;
    return Math.round((price * rate) / (1 - Math.pow(1 + rate, -term)));
  };

  const calculateInsurance = (price, year) => {
    if (!price) return null;
    const age = year ? new Date().getFullYear() - year : 3;
    const baseRate = age < 2 ? 0.04 : age < 4 ? 0.035 : age < 7 ? 0.03 : 0.025;
    return Math.round((price * baseRate) / 12);
  };

  const calculateMatchScore = (car) => {
    let score = 0;
    const q = (criteria.query || '').toLowerCase();
    const make = (criteria.make || '').toLowerCase();
    const model = (criteria.model || '').toLowerCase();
    const title = (car.title || '').toLowerCase();
    const carMake = (car.make || '').toLowerCase();
    const carModel = (car.model || '').toLowerCase();
    if (make && carMake.includes(make)) score += 30;
    else if (make && title.includes(make)) score += 25;
    if (model && carModel.includes(model)) score += 30;
    else if (model && title.includes(model)) score += 25;
    if (q && title.includes(q)) score += 20;
    const minP = parseInt(criteria.minPrice) || 0;
    const maxP = parseInt(criteria.maxPrice) || 5000000;
    const price = car.price || 0;
    if (price >= minP && price <= maxP) {
      const midPrice = (minP + maxP) / 2;
      score += Math.max(0, 10 - Math.abs(price - midPrice) / midPrice * 10);
    }
    score += ((car.year || 2015) - 2010) * 0.5;
    if ((car.mileage || 100000) < 50000) score += 10;
    else if ((car.mileage || 100000) < 100000) score += 5;
    return score;
  };

  const getListingsWithScores = () => {
    return listings.map(car => ({ ...car, matchScore: calculateMatchScore(car) }))
      .sort((a, b) => sort === 'relevance' ? b.matchScore - a.matchScore : 0);
  };

  const scoredListings = getListingsWithScores();
  const topScore = scoredListings.length > 0 ? scoredListings[0].matchScore : 0;
  const totalPages = Math.ceil(total / pageSize);
  const hasActiveSearch =
  searchQuery ||
  searchMake ||
  searchMinPrice ||
  searchMaxPrice ||
  searchBodyType ||
  searchMinYear ||
  searchFuel ||
  searchTransmission;
  const popularMakes = ['Toyota', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Ford', 'Audi', 'Hyundai', 'Kia'];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="results-page">
    <h1 className="sr-only">Used Cars for Sale in South Africa</h1>
    <p className="sr-only">
    Compare used cars from trusted South African dealers, book comprehensive inspections as well as get quotes on car & life insurance/protection
    </p>
      {/* Search Header */}
      <div className="bg-gray-900 pt-16 pb-6">
        <div className="container">
          <h1 className="text-2xl font-bold text-white mb-4" data-testid="results-heading">
            {total > 0 ? `${total.toLocaleString()} Vehicles Available` : 'Search Cars'}
            {criteria.make && <span className="text-yellow-400"> - {criteria.make}{criteria.model ? ` ${criteria.model}` : ''}</span>}
            {criteria.bodyType && !criteria.make && <span className="text-yellow-400"> - {criteria.bodyType}</span>}
          </h1>

          {/* Search Bar */}
          <form onSubmit={handleSearchSubmit} data-testid="search-form">
            <div className="grid grid-cols-2 lg:grid-cols-12 gap-2">
              <div className="col-span-2 lg:col-span-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search make, model, keyword..."
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition"
                  data-testid="search-input"
                />
              </div>
              <div className="lg:col-span-2">
                <select value={searchMake} onChange={(e) => setSearchMake(e.target.value)}
                  className="w-full px-3 py-3 bg-white/10 border border-gray-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-yellow-400 text-sm"
                  data-testid="make-select">
                  <option value="" className="text-gray-900">All Makes</option>
                  {makes.map(m => <option key={m} value={m} className="text-gray-900">{m}</option>)}
                </select>
              </div>
              <div className="lg:col-span-2">
                <select
                  value={searchMinPrice ? `${searchMinPrice}-${searchMaxPrice}` : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [min, max] = e.target.value.split('-');
                      setSearchMinPrice(min);
                      setSearchMaxPrice(max || '');
                    } else { setSearchMinPrice(''); setSearchMaxPrice(''); }
                  }}
                  className="w-full px-3 py-3 bg-white/10 border border-gray-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-yellow-400 text-sm"
                  data-testid="price-select">
                  <option value="" className="text-gray-900">Any Price</option>
                  <option value="0-100000" className="text-gray-900">Under R100k</option>
                  <option value="100000-200000" className="text-gray-900">R100k - R200k</option>
                  <option value="200000-350000" className="text-gray-900">R200k - R350k</option>
                  <option value="350000-500000" className="text-gray-900">R350k - R500k</option>
                  <option value="500000-750000" className="text-gray-900">R500k - R750k</option>
                  <option value="750000-1000000" className="text-gray-900">R750k - R1M</option>
                  <option value="1000000-" className="text-gray-900">Over R1M</option>
                </select>
              </div>
              <div className="lg:col-span-2">
                <select value={searchMinYear} onChange={(e) => setSearchMinYear(e.target.value)}
                  className="w-full px-3 py-3 bg-white/10 border border-gray-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-yellow-400 text-sm"
                  data-testid="year-select">
                  <option value="" className="text-gray-900">Any Year</option>
                  <option value="2024" className="text-gray-900">2024+</option>
                  <option value="2022" className="text-gray-900">2022+</option>
                  <option value="2020" className="text-gray-900">2020+</option>
                  <option value="2018" className="text-gray-900">2018+</option>
                  <option value="2015" className="text-gray-900">2015+</option>
                  <option value="2010" className="text-gray-900">2010+</option>
                  <option value="2005" className="text-gray-900">2005+</option>
                </select>
              </div>
              <div className="lg:col-span-2">
                <button type="submit"
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-500 transition flex items-center justify-center gap-2"
                  data-testid="search-submit-btn">
                  <Search className="w-5 h-5" /> Search
                </button>
              </div>
            </div>
            {/* Second row: Body Type, Fuel, Transmission */}
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mt-2">
              <select value={searchBodyType} onChange={(e) => setSearchBodyType(e.target.value)}
                className="px-3 py-2 bg-white/10 border border-gray-700 rounded-lg text-white text-sm appearance-none cursor-pointer focus:outline-none focus:border-yellow-400"
                data-testid="body-type-select">
                <option value="" className="text-gray-900">Body Type</option>
                <option value="SUV" className="text-gray-900">SUV</option>
                <option value="Sedan" className="text-gray-900">Sedan</option>
                <option value="Hatchback" className="text-gray-900">Hatchback</option>
                <option value="Bakkie" className="text-gray-900">Bakkie</option>
                <option value="Double Cab" className="text-gray-900">Double Cab</option>
                <option value="Crossover" className="text-gray-900">Crossover</option>
                <option value="Coupe" className="text-gray-900">Coupe</option>
                <option value="Van" className="text-gray-900">Van / MPV</option>
              </select>
              <select value={searchFuel} onChange={(e) => setSearchFuel(e.target.value)}
                className="px-3 py-2 bg-white/10 border border-gray-700 rounded-lg text-white text-sm appearance-none cursor-pointer focus:outline-none focus:border-yellow-400"
                data-testid="fuel-select">
                <option value="" className="text-gray-900">Fuel Type</option>
                <option value="Petrol" className="text-gray-900">Petrol</option>
                <option value="Diesel" className="text-gray-900">Diesel</option>
                <option value="Hybrid" className="text-gray-900">Hybrid</option>
                <option value="Electric" className="text-gray-900">Electric</option>
              </select>
              <select value={searchTransmission} onChange={(e) => setSearchTransmission(e.target.value)}
                className="px-3 py-2 bg-white/10 border border-gray-700 rounded-lg text-white text-sm appearance-none cursor-pointer focus:outline-none focus:border-yellow-400"
                data-testid="transmission-select">
                <option value="" className="text-gray-900">Transmission</option>
                <option value="Manual" className="text-gray-900">Manual</option>
                <option value="Automatic" className="text-gray-900">Automatic</option>
              </select>
            </div>
          </form>

          {/* Active filters / clear */}
          {hasActiveSearch && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {searchQuery && (
                <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-sm flex items-center gap-1">
                  "{searchQuery}" <X className="w-3 h-3 cursor-pointer" onClick={() => { setSearchQuery(''); }} />
                </span>
              )}
              {searchMake && (
                <span className="px-3 py-1 bg-blue-400/20 text-blue-400 rounded-full text-sm flex items-center gap-1">
                  {searchMake} <X className="w-3 h-3 cursor-pointer" onClick={() => { setSearchMake(''); }} />
                </span>
              )}
              {searchMinPrice && (
                <span className="px-3 py-1 bg-green-400/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                  R{parseInt(searchMinPrice).toLocaleString()}{searchMaxPrice ? ` - R${parseInt(searchMaxPrice).toLocaleString()}` : '+'} <X className="w-3 h-3 cursor-pointer" onClick={() => { setSearchMinPrice(''); setSearchMaxPrice(''); }} />
                </span>
              )}
              {searchBodyType && (
                <span className="px-3 py-1 bg-purple-400/20 text-purple-400 rounded-full text-sm flex items-center gap-1">
                  {searchBodyType} <X className="w-3 h-3 cursor-pointer" onClick={() => { setSearchBodyType(''); navigate(`/results`); }} />
                </span>
              )}
              <button onClick={handleClearSearch} className="text-gray-400 text-sm hover:text-white transition">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters + Sort Bar */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{total.toLocaleString()} results</span>
              {sources.length > 0 && (
                <span className="hidden md:inline">from {(() => {
                  const sorted = [...sources].sort((a,b) => (b.count || 0) - (a.count || 0));
                  const top = sorted.slice(0, 4).map(s => typeof s === 'object' ? s.source : s);
                  const rest = sorted.length - 4;
                  return top.join(', ') + (rest > 0 ? ` +${rest} more` : '');
                })()}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`md:hidden px-3 py-2 border rounded-lg text-sm flex items-center gap-1 ${showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-600'}`}
              >
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
              <div className={`${showFilters ? 'flex' : 'hidden'} md:flex items-center gap-2`}>
                <select value={filters.fuel} onChange={(e) => handleFilterChange('fuel', e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="filter-fuel">
                  <option value="">Any Fuel</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="electric">Electric</option>
                </select>
                <select value={filters.transmission} onChange={(e) => handleFilterChange('transmission', e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="filter-transmission">
                  <option value="">Any Trans.</option>
                  <option value="manual">Manual</option>
                  <option value="automatic">Automatic</option>
                </select>
              </div>
              <select value={sort} onChange={(e) => handleSortChange(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="sort-select">
                <option value="relevance">Relevance</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="year-desc">Year: Newest</option>
                <option value="year-asc">Year: Oldest</option>
                <option value="mileage-asc">Mileage: Lowest</option>
              </select>
              <div className="hidden md:flex border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-400'}`} data-testid="view-grid">
                  <Grid className="w-5 h-5" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-400'}`} data-testid="view-list">
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="container py-6">
        {listings.length === 0 && !loading ? (
          <div className="text-center py-12" data-testid="no-results">
            <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {hasActiveSearch ? 'No Matching Vehicles' : 'Find Your Next Car'}
            </h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              {hasActiveSearch 
                ? 'Try adjusting your search or filters to find more results.'
                : 'Use the search bar above to find vehicles across South Africa\'s top dealers.'}
            </p>

            {/* Quick Make Buttons */}
            <div className="max-w-2xl mx-auto">
              <p className="text-sm font-medium text-gray-400 mb-3">Popular Makes</p>
              <div className="flex flex-wrap justify-center gap-2">
                {popularMakes.map(make => (
                  <button
                    key={make}
                    onClick={() => navigate(`/results?make=${make}`)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition"
                    data-testid={`quick-make-${make.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {make}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Price Ranges */}
            <div className="max-w-2xl mx-auto mt-6">
              <p className="text-sm font-medium text-gray-400 mb-3">Budget Range</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { label: 'Under R150k', min: '0', max: '150000' },
                  { label: 'R150k - R300k', min: '150000', max: '300000' },
                  { label: 'R300k - R500k', min: '300000', max: '500000' },
                  { label: 'R500k+', min: '500000', max: '' },
                ].map(b => (
                  <button
                    key={b.label}
                    onClick={() => navigate(`/results?min_price=${b.min}${b.max ? `&max_price=${b.max}` : ''}`)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-green-500 hover:text-green-600 hover:bg-green-50 transition"
                    data-testid={`quick-budget-${b.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
              {scoredListings.map((car, idx) => {
                const isBestMatch = hasActiveSearch && car.matchScore >= topScore * 0.85 && car.matchScore > 30 && idx < 6;
                const isGoodValue = car.price && car.price < 300000 && car.year >= 2019;
                const insuranceEst = calculateInsurance(car.price, car.year);
                
                return (
                <div 
                  key={car.id || idx} 
                  className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition group relative cursor-pointer"
                  onClick={() => navigate('/car-detail', { state: { car } })}
                  data-testid={`car-card-${idx}`}
                >
                  <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
                    {isBestMatch && (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded font-bold flex items-center gap-1 shadow">
                        <Award className="w-3 h-3" /> Best Match
                      </span>
                    )}
                    {isGoodValue && (
                      <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded font-bold flex items-center gap-1 shadow">
                        <TrendingUp className="w-3 h-3" /> Good Value
                      </span>
                    )}
                  </div>

                  <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                    {car.image_url ? (
                      <img
                        src={car.image_url}
                        alt={car.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"><svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"></path></svg><span class="text-gray-400 text-sm mt-2">${car.make || 'Vehicle'}</span></div>`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Car className="w-16 h-16 text-gray-300" />
                        <span className="text-gray-400 text-sm mt-2">{car.make || 'Vehicle'}</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded font-semibold shadow">
                        {car.source || car.dealer || 'Dealer'}
                      </span>
                    </div>
                    {car.condition && (
                      <div className="absolute bottom-3 left-3">
                        <span className={`px-2 py-1 text-xs rounded font-bold shadow ${car.condition === 'New' ? 'bg-green-500 text-white' : car.condition === 'Demo' ? 'bg-orange-500 text-white' : 'bg-gray-700 text-white'}`}>
                          {car.condition}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-blue-600 font-medium">{car.source || car.dealer}</span>
                      {car.location && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{car.location}
                        </span>
                      )}
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-blue-600 transition">
                      {car.title || `${car.year} ${car.make} ${car.model}`}
                    </h3>
                    
                    <div className="text-2xl font-bold text-blue-600 mb-1">{formatPrice(car.price)}</div>

                    {car.price && (
                      <div className="bg-gray-50 rounded-lg p-2 mb-3 text-xs">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Est. Finance:</span>
                          <span className="font-semibold text-gray-700">R{calculateMonthly(car.price)?.toLocaleString()}/pm</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Est. Insurance:</span>
                          <span className="font-semibold text-gray-700">R{insuranceEst?.toLocaleString()}/pm</span>
                        </div>
                        <p className="text-gray-400 mt-1 text-center">(72m @ 13% | Comprehensive)</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                      {car.year && <span className="px-2 py-1 bg-gray-100 rounded">{car.year}</span>}
                      {car.mileage && <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded"><Gauge className="w-3 h-3" />{formatMileage(car.mileage)}</span>}
                      {car.transmission && <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded"><Settings className="w-3 h-3" />{car.transmission}</span>}
                      {car.fuel_type && <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded"><Fuel className="w-3 h-3" />{car.fuel_type}</span>}
                    </div>

                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleInspectionClick(car)} className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg font-medium hover:bg-green-700 transition flex items-center justify-center gap-1" data-testid="inspect-btn">
                        <ClipboardCheck className="w-4 h-4" /> Inspect
                      </button>
                      <button onClick={() => handleInsuranceClick(car)} className="flex-1 px-3 py-2 bg-purple-600 text-white text-sm rounded-lg font-medium hover:bg-purple-700 transition flex items-center justify-center gap-1" data-testid="insure-btn">
                        <Shield className="w-4 h-4" /> Insure
                      </button>
                    </div>
                    
                    <div onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleFinanceClick(car)} className="w-full mt-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-1" data-testid="finance-btn">
                        <DollarSign className="w-4 h-4" /> Calculate Finance
                      </button>
                      {car.source_url && car.source_url !== '#' && (
                        <button onClick={() => handleDealerClick(car)} className="w-full mt-2 px-3 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg font-medium hover:bg-gray-50 transition flex items-center justify-center gap-1" data-testid="dealer-btn">
                          <ExternalLink className="w-4 h-4" /> View on {car.dealer || 'Dealer Site'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1} className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-10 h-10 rounded-lg font-medium ${page === pageNum ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="spinner"></div>
            <p className="mt-4 font-semibold">Searching across dealers...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;
